import { cn } from 'lib/utils'
import { Loader2, Send } from 'lucide-react'
import type { ButtonHTMLAttributes, FormEvent, ReactNode } from 'react'

interface ComposerRowProps {
  /**
   * Icon shown at the left edge of the row. Conventions:
   * - writing/compose inputs: <PenLine size={16} className="text-paper-dim" />
   * - search: <Search size={16} className="text-paper-dim" />
   * - chats with attachments: an <ImagePlus> button
   * - plain chats: omit
   */
  leading?: ReactNode
  /**
   * Trailing action(s) — typically <ComposerSendButton> for chat surfaces.
   * Editing / writing fields leave this empty; they auto-save on blur.
   */
  trailing?: ReactNode
  /**
   * Slot rendered ABOVE the input row, inside the focus border. Used by
   * SideChat for the attachments preview strip and the section-context chip.
   */
  above?: ReactNode
  /**
   * 'form' for chat composers that dispatch on submit; 'div' for editing
   * fields that have no submit affordance.
   */
  as?: 'form' | 'div'
  onSubmit?: (e: FormEvent<HTMLFormElement>) => void
  /**
   * Vertical alignment of the input row. `end` for multiline chat composers;
   * `center` for single-line prompt boxes (TopicSwitcher, search).
   */
  inputAlign?: 'center' | 'end'
  className?: string
  children: ReactNode
}

/**
 * Canonical input chrome for any surface where the user types — chats, prompt
 * boxes, brand fields, captions, notes. One source of truth so the focus
 * border, padding, and background stay aligned across the app.
 *
 * Spec (locked):
 * - 1px `--line-bright` border at rest, 1px `--primary` (acid) border on focus.
 * - No soft halo, no offset ring, no `--acid-soft` shadow. Single clean line.
 * - The `!` on `focus-within:border-primary` is required: `.border-line-bright`
 *   ships outside any `@layer` in design-system.css and would otherwise win
 *   the cascade against Tailwind's variant utility (see foundations.md
 *   "Shim-class override gotcha").
 */
export function ComposerRow({
  leading,
  trailing,
  above,
  as = 'form',
  onSubmit,
  inputAlign = 'end',
  className,
  children,
}: ComposerRowProps) {
  const sharedClassName = cn(
    'rounded-[10px] border border-line-bright bg-bg-3 transition-[border-color] focus-within:border-primary!',
    className
  )

  const body = (
    <>
      {above && <div className="px-3 pt-2">{above}</div>}
      <div
        className={cn(
          'flex gap-2 px-3',
          inputAlign === 'center' ? 'items-center py-2' : 'items-end'
        )}
      >
        {leading && (
          <div className={cn('flex-shrink-0', inputAlign === 'end' && 'my-1.5')}>{leading}</div>
        )}
        <div className="min-w-0 flex-1">{children}</div>
        {trailing && <div className="flex-shrink-0">{trailing}</div>}
      </div>
    </>
  )

  if (as === 'form') {
    return (
      <form onSubmit={onSubmit} className={sharedClassName}>
        {body}
      </form>
    )
  }
  return <div className={sharedClassName}>{body}</div>
}

interface ComposerSendButtonProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'children'
> {
  loading?: boolean
  /** Icon size in px. Defaults to 18 to match AssistantChat/SideChat. */
  iconSize?: number
}

/**
 * Canonical Send button for chat composers. Use ONLY on surfaces where
 * pressing submits a message (AssistantChat, SideChat, TopicSwitcher's
 * ask-Taka prompt). Editing fields auto-save and do not get this button.
 *
 * Filled acid square with ink-on-acid icon — same shape and weight everywhere
 * so users learn one "send" affordance. Disabled state drops to a neutral
 * surface (bg-bg-4) instead of a faded acid, so it never reads as "I'm still
 * the primary action, just dimmer."
 */
export function ComposerSendButton({
  loading,
  iconSize = 14,
  className,
  type = 'submit',
  disabled,
  ...rest
}: ComposerSendButtonProps) {
  const enabled = !disabled && !loading
  return (
    <button
      type={type}
      disabled={disabled || loading}
      aria-label="Send"
      className={cn(
        'my-1 inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-[8px] transition-opacity',
        enabled
          ? 'text-ink-on-acid bg-acid hover:opacity-90'
          : 'cursor-not-allowed bg-bg-4 text-paper-faint',
        className
      )}
      {...rest}
    >
      {loading ? <Loader2 size={iconSize} className="animate-spin" /> : <Send size={iconSize} />}
    </button>
  )
}
